/**
 * Create Notification Modal Component
 * Modal สำหรับสร้างและส่ง notification ใหม่
 */

import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  FiBell,
  FiX,
  FiSend,
  FiAlertCircle,
  FiInfo,
  FiCheckCircle,
  FiXCircle,
  FiUsers,
  FiEye
} from 'react-icons/fi';
import { Button } from '../../ui/Button';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { API_URL } from '../../../services/api';

// Types
enum NotificationType {
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  ERROR = 'ERROR'
}

enum TargetUsers {
  ALL = 'ALL',
  ADMINS = 'ADMINS',
  MEMBERS = 'MEMBERS',
  SPECIFIC = 'SPECIFIC'
}

interface CreateNotificationData {
  title: string;
  message: string;
  notification_type: NotificationType;
  target_users: TargetUsers;
  specific_user_ids?: string[];
}

interface CreateNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  token: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

const CreateNotificationModal: React.FC<CreateNotificationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  token
}) => {
  // Form state
  const [formData, setFormData] = useState<CreateNotificationData>({
    title: '',
    message: '',
    notification_type: NotificationType.INFO,
    target_users: TargetUsers.ALL,
    specific_user_ids: []
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load users when target is SPECIFIC
  useEffect(() => {
    if (formData.target_users === TargetUsers.SPECIFIC && isOpen) {
      loadUsers();
    }
  }, [formData.target_users, isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        title: '',
        message: '',
        notification_type: NotificationType.INFO,
        target_users: TargetUsers.ALL,
        specific_user_ids: []
      });
      setErrors({});
      setShowPreview(false);
    }
  }, [isOpen]);

  // Load users for specific targeting
  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('ไม่สามารถโหลดรายชื่อผู้ใช้ได้');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Load users error:', error);
      toast.error('ไม่สามารถโหลดรายชื่อผู้ใช้ได้');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'กรุณากรอกหัวเรื่อง';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'กรุณากรอกข้อความ';
    }

    if (formData.target_users === TargetUsers.SPECIFIC && (!formData.specific_user_ids || formData.specific_user_ids.length === 0)) {
      newErrors.specific_user_ids = 'กรุณาเลือกผู้ใช้อย่างน้อย 1 คน';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      

      // Prepare request body
      const requestBody: any = {
        title: formData.title.trim(),
        message: formData.message.trim(),
        notification_type: formData.notification_type,
        target_users: formData.target_users
      };

      // Add specific_user_ids only if target is SPECIFIC
      if (formData.target_users === TargetUsers.SPECIFIC) {
        requestBody.specific_user_ids = formData.specific_user_ids;
      }

      const response = await fetch(`${API_URL}/api/admin/notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || result.message || 'ไม่สามารถสร้างการแจ้งเตือนได้');
      }

      toast.success('สร้างและส่งการแจ้งเตือนเรียบร้อยแล้ว');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Create notification error:', error);
      toast.error(error.message || 'ไม่สามารถสร้างการแจ้งเตือนได้');
    } finally {
      setLoading(false);
    }
  };

  // Handle input change
  const handleChange = (field: keyof CreateNotificationData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle user selection
  const handleUserToggle = (userId: string) => {
    setFormData(prev => {
      const currentIds = prev.specific_user_ids || [];
      const newIds = currentIds.includes(userId)
        ? currentIds.filter(id => id !== userId)
        : [...currentIds, userId];
      return { ...prev, specific_user_ids: newIds };
    });
  };

  // Get notification type icon
  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case NotificationType.INFO:
        return <FiInfo className="h-5 w-5" />;
      case NotificationType.SUCCESS:
        return <FiCheckCircle className="h-5 w-5" />;
      case NotificationType.WARNING:
        return <FiAlertCircle className="h-5 w-5" />;
      case NotificationType.ERROR:
        return <FiXCircle className="h-5 w-5" />;
    }
  };

  // Get notification type color
  const getTypeColor = (type: NotificationType) => {
    switch (type) {
      case NotificationType.INFO:
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case NotificationType.SUCCESS:
        return 'bg-green-50 border-green-200 text-green-800';
      case NotificationType.WARNING:
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case NotificationType.ERROR:
        return 'bg-red-50 border-red-200 text-red-800';
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={loading ? () => {} : onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100">
                      <FiBell className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        สร้างการแจ้งเตือน
                      </Dialog.Title>
                      <p className="text-sm text-gray-500">
                        สร้างและส่งการแจ้งเตือนให้กับผู้ใช้งาน
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
                  >
                    <FiX className="h-6 w-6" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {showPreview ? (
                    /* Preview Mode */
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">ตัวอย่างการแจ้งเตือน</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPreview(false)}
                        >
                          แก้ไข
                        </Button>
                      </div>
                      <div className={`p-6 rounded-lg border-2 ${getTypeColor(formData.notification_type)}`}>
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mt-0.5">
                            {getTypeIcon(formData.notification_type)}
                          </div>
                          <div className="ml-3 flex-1">
                            <h4 className="text-lg font-semibold mb-2">{formData.title}</h4>
                            <p className="text-sm whitespace-pre-wrap">{formData.message}</p>
                            <div className="mt-4 pt-4 border-t border-current/20">
                              <div className="flex items-center text-xs">
                                <FiUsers className="h-4 w-4 mr-1" />
                                <span>ส่งถึง: </span>
                                <span className="font-semibold ml-1">
                                  {formData.target_users === TargetUsers.ALL && 'ทุกคน'}
                                  {formData.target_users === TargetUsers.ADMINS && 'ผู้ดูแลระบบ'}
                                  {formData.target_users === TargetUsers.MEMBERS && 'สมาชิก'}
                                  {formData.target_users === TargetUsers.SPECIFIC && `${formData.specific_user_ids?.length || 0} คน`}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Form Mode */
                    <div className="space-y-6">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          หัวเรื่อง <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => handleChange('title', e.target.value)}
                          placeholder="เช่น: ระบบปรับปรุง"
                          disabled={loading}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 ${
                            errors.title ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {errors.title && (
                          <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                        )}
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          ข้อความ <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={formData.message}
                          onChange={(e) => handleChange('message', e.target.value)}
                          placeholder="เช่น: ระบบจะปิดปรับปรุงวันที่ 10 ตุลาคม 2025"
                          rows={4}
                          disabled={loading}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 ${
                            errors.message ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {errors.message && (
                          <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                        )}
                      </div>

                      {/* Notification Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          ประเภทการแจ้งเตือน
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          {Object.values(NotificationType).map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => handleChange('notification_type', type)}
                              disabled={loading}
                              className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all disabled:opacity-50 ${
                                formData.notification_type === type
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              {getTypeIcon(type)}
                              <span className="ml-2 font-medium capitalize">{type}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Target Users */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          กลุ่มเป้าหมาย
                        </label>
                        <select
                          value={formData.target_users}
                          onChange={(e) => handleChange('target_users', e.target.value as TargetUsers)}
                          disabled={loading}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                        >
                          <option value={TargetUsers.ALL}>ทุกคน</option>
                          <option value={TargetUsers.ADMINS}>ผู้ดูแลระบบ</option>
                          <option value={TargetUsers.MEMBERS}>สมาชิก</option>
                          <option value={TargetUsers.SPECIFIC}>เลือกเฉพาะคน</option>
                        </select>
                      </div>

                      {/* Specific Users Selection */}
                      {formData.target_users === TargetUsers.SPECIFIC && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            เลือกผู้ใช้ <span className="text-red-500">*</span>
                          </label>
                          {loadingUsers ? (
                            <div className="text-center py-4 text-gray-500">
                              กำลังโหลดรายชื่อผู้ใช้...
                            </div>
                          ) : (
                            <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                              {users.length === 0 ? (
                                <div className="text-center py-4 text-gray-500">
                                  ไม่พบผู้ใช้
                                </div>
                              ) : (
                                <div className="divide-y divide-gray-200">
                                  {users.map((user) => (
                                    <label
                                      key={user.id}
                                      className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={formData.specific_user_ids?.includes(user.id)}
                                        onChange={() => handleUserToggle(user.id)}
                                        disabled={loading}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                      />
                                      <div className="ml-3">
                                        <div className="text-sm font-medium text-gray-900">
                                          {user.firstName} {user.lastName}
                                        </div>
                                        <div className="text-sm text-gray-500">{user.email}</div>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {errors.specific_user_ids && (
                            <p className="mt-1 text-sm text-red-600">{errors.specific_user_ids}</p>
                          )}
                          {formData.specific_user_ids && formData.specific_user_ids.length > 0 && (
                            <p className="mt-2 text-sm text-gray-600">
                              เลือกแล้ว {formData.specific_user_ids.length} คน
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <Button
                    variant="ghost"
                    onClick={onClose}
                    disabled={loading}
                  >
                    ยกเลิก
                  </Button>
                  <div className="flex space-x-3">
                    {!showPreview && (
                      <Button
                        variant="secondary"
                        leftIcon={<FiEye />}
                        onClick={() => {
                          if (validateForm()) {
                            setShowPreview(true);
                          }
                        }}
                        disabled={loading}
                      >
                        ดูตัวอย่าง
                      </Button>
                    )}
                    <Button
                      variant="default"
                      leftIcon={<FiSend />}
                      onClick={handleSubmit}
                      loading={loading}
                      loadingText="กำลังส่ง..."
                    >
                      ส่งการแจ้งเตือน
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CreateNotificationModal;
